import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FILTER_RESOURCE_ADMINISTRATIVE_REGION = 'administrative-region';
export const BD_ADMINISTRATIVE_REGION_CONDITION_ID = 'administrativeRegion';
export const FilterConditionAdministrativeRegionIdPluginType: FilterConditionPluginSpecification<
  number[],
  FilterConditionAdministrativeRegionIdDto,
  BdAdministrativeRegionCondition
> = {
  type: 'administrativeRegionIds',
  category: 'person',
  displayName: { de: 'Regierungsbezirke' },
  sortingIndex: 2,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionAdministrativeRegionIdDto,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionAdministrativeRegionIdPlugin(
        obj.administrativeRegionIds,
      );
  },
  fromCondition(
    jsonObj: BdAdministrativeRegionCondition,
  ): FilterConditionPluginImpl<number[]> {
    return new FilterConditionAdministrativeRegionIdPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<number[]> {
    return new FilterConditionAdministrativeRegionIdPlugin([]);
  },
};

export class FilterConditionAdministrativeRegionIdPlugin extends FilterConditionPluginImpl<
  number[],
  FilterConditionAdministrativeRegionIdDto
> {
  static override Specification: FilterConditionPluginSpecification<
    number[],
    FilterConditionAdministrativeRegionIdDto,
    BdAdministrativeRegionCondition
  > = FilterConditionAdministrativeRegionIdPluginType;

  constructor(private administrativeRegionIds: number[]) {
    super(FilterConditionAdministrativeRegionIdPluginType.type);
  }

  clone(): FilterConditionAdministrativeRegionIdPlugin {
    return new FilterConditionAdministrativeRegionIdPlugin([
      ...(this.administrativeRegionIds ? this.administrativeRegionIds : []),
    ]);
  }

  override equals(other: FilterConditionAdministrativeRegionIdPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(
        this.administrativeRegionIds,
        other.administrativeRegionIds,
      )
    );
  }

  override validationToText(): string {
    if (this.administrativeRegionIds.length === 0) {
      return 'Mindestens einen Regierungsbezirk auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdAdministrativeRegionCondition(this.administrativeRegionIds, []);
  }

  toJSON(): FilterConditionAdministrativeRegionIdDto | null {
    return {
      type: this.type,
      administrativeRegionIds: this.administrativeRegionIds,
    };
  }

  toText(): string | null {
    switch (this.administrativeRegionIds.length) {
      case 0:
        return 'keine Regierungsbezirke';
      case 1:
        return 'ein Regierungsbezirk';
      default:
        return `${this.administrativeRegionIds.length} Regierungsbezirke`;
    }
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (this.administrativeRegionIds.length === 0) return {};
    return {
      [FILTER_RESOURCE_ADMINISTRATIVE_REGION]:
        this.administrativeRegionIds.map(String),
    };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.administrativeRegionIds.map((id) => {
      const key = String(id);
      return resolvedNames?.get(key) ?? key;
    });
    return { title, items };
  }

  toValue(): number[] {
    return this.administrativeRegionIds;
  }
}

export class BdAdministrativeRegionCondition extends BdContentCondition<number> {
  constructor(includes: number[], excludes: number[]) {
    super(BD_ADMINISTRATIVE_REGION_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionAdministrativeRegionIdDto {
  type: string;
  administrativeRegionIds: number[];
}
